import React, { useEffect, useState } from 'react';
import SiteFooter from '../components/SiteFooter';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import { CheckCircle, Share2, Copy, Loader2, ExternalLink } from 'lucide-react';
import logoImg from '../assets/logo-haosong.jpg';
import LanguageSwitcher from '../components/LanguageSwitcher';

const HK_DISTRICT_GROUPS = [
  {
    region: '香港島',
    areas: ['上環/中環/金鐘', '灣仔/銅鑼灣/跑馬地', '北角/鰂魚涌', '太古/西灣河/筲箕灣', '柴灣/小西灣', '香港仔/鴨脷洲', '薄扶林/數碼港', '山頂/半山區'],
  },
  {
    region: '九龍',
    areas: ['尖沙咀/佐敦', '油麻地', '旺角/大角咀', '紅磡/何文田', '九龍城/土瓜灣', '啟德/九龍灣', '黃大仙/新蒲崗', '彩虹/慈雲山', '觀塘/藍田', '深水埗/長沙灣', '石硤尾/九龍塘'],
  },
  {
    region: '新界',
    areas: ['荃灣', '葵涌/葵芳', '青衣', '屯門', '元朗', '天水圍', '大埔', '沙田/大圍', '馬鞍山', '上水/粉嶺', '將軍澳', '西貢', '東涌', '離島'],
  },
];

const AGE_OPTIONS = ['16', '17', '18', '19', '20', '21', '22', '23', '24', '25 以上'];

// TODO: 上載創辦人影片到 R2 後更新此連結
const FOUNDER_VIDEO_URL = 'https://shop.deliciousexpress.com.hk/media/media_coverage/掃碼登記.mp4';

const MEDIA_LINKS = [
  { label: 'TVB 鏗鏘集：疫下社企（好餸社企）', url: 'https://www.youtube.com/watch?v=Ln15FIjNvKk' },
  { label: '好餸 X 魚類統營處及蔬菜統營處', url: 'https://www.youtube.com/watch?v=p2Pg4J70WF8' },
  { label: '社企「好餸」僱主分享', url: 'https://www.youtube.com/watch?v=lBYCnPINkAk' },
  { label: '🔍【#透視社福】👨‍👨‍👧‍👦好餸🥰', url: 'https://www.youtube.com/watch?v=uql5TFtmEzc' },
  { label: '好餸（社企）《夥拍良心業主開店計劃》', url: 'https://www.youtube.com/watch?v=nvNxXwvs8Sc' },
  { label: 'U Food：獅球嘜聯乘「好餸社企」推出健康餸菜包慈善義賣', url: 'https://ufood.com.hk/unboxing/detail/20049009/獅球嘜聯乘-好餸社企-推出健康餸菜包慈善義賣-蒜蓉牛油雞翼-菠蘿蜜味牛仔骨-酒香糖醋排骨' },
  { label: 'CTGoodJobs：社會企業 由音樂教育到創辦「好餸」餸菜包', url: 'https://ngo.ctgoodjobs.hk/article/1327-32399/社會企業-由音樂教育到創辦-好餸-餸菜包-撞板多次但充滿意義' },
];

interface SlotOption {
  slot: string;
  label: string;
  date: string;
  time: string;
  available: number;
  full: boolean;
}

const SenPlanPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [slots, setSlots] = useState<SlotOption[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [form, setForm] = useState({
    parentName: '',
    phone: searchParams.get('phone') || '',
    gender: '',
    age: '',
    district: '',
    attendees: '1',
    sessionSlot: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string; deepLink?: string } | null>(null);
  const [shareTooltip, setShareTooltip] = useState('');

  useEffect(() => {
    apiFetch('/api/public/goodstore/slots')
      .then(r => r.json())
      .then((data: any) => {
        if (data.success && Array.isArray(data.slots)) {
          const loaded = data.slots as SlotOption[];
          setSlots(loaded);
          const firstAvailable = loaded.find(s => !s.full);
          if (firstAvailable) {
            setForm(prev => ({ ...prev, sessionSlot: firstAvailable.slot }));
          }
        }
      })
      .finally(() => setSlotsLoading(false));
  }, []);

  const handleShare = async () => {
    const shareData = {
      title: t('content:senPlan.shareTitle'),
      text: t('content:senPlan.shareText'),
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setShareTooltip(t('content:senPlan.linkCopied'));
        setTimeout(() => setShareTooltip(''), 2000);
      }
    } catch {
      // ignore
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareTooltip(t('content:senPlan.linkCopied'));
      setTimeout(() => setShareTooltip(''), 2000);
    } catch {
      // ignore
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);
    try {
      const res = await apiFetch('/api/public/goodstore/enroll', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'goodstore-landing' }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult({
          success: true,
          message: t('content:senPlan.submittedMsg'),
          deepLink: data.deepLink,
        });
      } else {
        setResult({ success: false, message: data.error || t('content:senPlan.submitFailed') });
      }
    } catch {
      setResult({ success: false, message: t('content:senPlan.networkError') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5] text-stone-800">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-stone-100 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoImg} alt={t('content:senPlan.logoAlt')} className="h-12 w-auto rounded-lg shadow-sm" />
            <div>
              <h1 className="text-lg md:text-xl font-bold text-[#C2410C]">{t('content:senPlan.brand')}</h1>
              <p className="text-[10px] md:text-xs text-stone-500">{t('content:senPlan.since')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher size="sm" />
            <div className="relative">
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 bg-[#C2410C] text-white rounded-full text-sm font-bold hover:bg-[#9a3412] transition"
              >
                <Share2 size={16} />
                {t('content:senPlan.share')}
              </button>
              {shareTooltip && (
                <span className="absolute right-0 -bottom-8 bg-stone-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                  {shareTooltip}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 md:py-12 space-y-10">
        {/* Hero */}
        <section className="text-center space-y-4">
          <p className="text-[#C2410C] font-bold tracking-wider">{t('content:senPlan.kicker')}</p>
          <h2 className="text-3xl md:text-5xl font-bold text-stone-800 leading-tight">
            {t('content:senPlan.title')}
          </h2>
          <p className="text-lg md:text-xl text-stone-600 max-w-2xl mx-auto leading-relaxed">
            {t('content:senPlan.intro')}
          </p>
        </section>

        {/* Founder Video */}
        <section className="bg-white rounded-2xl shadow-xl border border-stone-100 overflow-hidden">
          <div className="aspect-[4/3] bg-stone-900 relative">
            <video
              src={FOUNDER_VIDEO_URL}
              controls
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover"
              poster={logoImg}
            >
              {t('content:senPlan.videoNotSupported')}
            </video>
          </div>
          <div className="p-5 md:p-6 text-center">
            <p className="text-stone-700 font-medium">{t('content:senPlan.videoCaption')}</p>
          </div>
        </section>

        {/* Media Coverage */}
        <section className="bg-white rounded-2xl shadow-lg border border-stone-100 p-6 md:p-8">
          <h3 className="text-xl md:text-2xl font-bold text-stone-800 mb-4">{t('content:senPlan.mediaHeading')}</h3>
          <p className="text-stone-600 mb-4 leading-relaxed">
            {t('content:senPlan.mediaIntro')}
          </p>
          <ul className="space-y-3">
            {MEDIA_LINKS.map((link, idx) => (
              <li key={idx}>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-2 text-[#C2410C] hover:underline font-medium"
                >
                  <ExternalLink size={18} className="shrink-0 mt-0.5" />
                  <span>{link.label}</span>
                </a>
              </li>
            ))}
          </ul>
        </section>

        {/* Enrollment Form */}
        <section className="bg-white rounded-2xl shadow-xl border-2 border-orange-100 p-6 md:p-8">
          <h3 className="text-2xl md:text-3xl font-bold text-stone-800 mb-2">{t('content:senPlan.formHeading')}</h3>
          <p className="text-stone-600 mb-6">
            {t('content:senPlan.venue')}
            <br />
            {t('content:senPlan.quota')}
          </p>

          {!slotsLoading && slots.length > 0 && slots.every(s => s.full) ? (
            <div className="text-center py-10 space-y-6">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                <span className="text-3xl">🙏</span>
              </div>
              <h4 className="text-2xl font-bold text-stone-800">{t('content:senPlan.fullTitle')}</h4>
              <p className="text-stone-600 max-w-md mx-auto leading-relaxed">
                {t('content:senPlan.fullBody1')}
                <br />
                {t('content:senPlan.fullBody2')}
              </p>
              <a
                href="https://wa.me/85257961378?text=%23SEN%E5%80%99%E8%A3%9C%20%E4%BD%A0%E5%A5%BD%EF%BC%8C%E6%88%91%E5%B0%8D%E3%80%8C%E5%A5%BD%E9%A4%B8%C2%B7%E9%84%B0%E9%87%8C%E3%80%8D%E8%A8%88%E5%8A%83%E6%9C%89%E8%88%88%E8%B6%A3%EF%BC%8C%E7%9B%AE%E5%89%8D%E6%B0%A1%E4%BB%8B%E6%9C%83%E5%90%8D%E9%A1%8D%E5%B7%B2%E6%BB%BF%EF%BC%8C%E5%B8%8C%E6%9C%9B%E5%80%99%E8%A3%9C%E3%80%82%E8%AB%8B%E8%81%AF%E7%B5%A1%E6%88%91%EF%BC%8C%E8%AC%9D%E8%AC%9D%EF%BC%81"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 text-white rounded-full font-bold text-lg hover:bg-green-700 transition"
              >
                <Share2 size={20} />
                {t('content:senPlan.waitlistBtn')}
              </a>
              <button
                type="button"
                onClick={handleShare}
                className="block mx-auto px-6 py-3 bg-white border-2 border-[#C2410C] text-[#C2410C] rounded-full font-bold hover:bg-orange-50 transition"
              >
                {t('content:senPlan.forwardBtn')}
              </button>
            </div>
          ) : result?.success ? (
            <div className="text-center py-10 space-y-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle size={40} className="text-green-600" />
              </div>
              <p className="text-xl font-bold text-green-700">{result.message}</p>
              {result.deepLink && (
                <a
                  href={result.deepLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 text-white rounded-full font-bold text-lg hover:bg-green-700 transition"
                >
                  <Share2 size={20} />
                  {t('content:senPlan.sendWhatsappBtn')}
                </a>
              )}
              <p className="text-sm text-stone-500 max-w-md mx-auto">
                {t('content:senPlan.sendHint')}
              </p>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#C2410C] text-white rounded-full font-bold hover:bg-[#9a3412] transition"
              >
                <Share2 size={18} />
                {t('content:senPlan.forwardBtn')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {result && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 font-bold">
                  {result.message}
                </div>
              )}

              <div>
                <label className="block text-base font-bold text-stone-700 mb-2">{t('content:senPlan.parentNameLabel')}</label>
                <input
                  type="text"
                  required
                  value={form.parentName}
                  onChange={e => setForm(prev => ({ ...prev, parentName: e.target.value }))}
                  className="w-full p-4 text-lg border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none min-h-[56px]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-base font-bold text-stone-700 mb-2">{t('content:senPlan.genderLabel')}</label>
                  <select
                    required
                    value={form.gender}
                    onChange={e => setForm(prev => ({ ...prev, gender: e.target.value }))}
                    className="w-full p-4 text-lg border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none min-h-[56px] bg-white"
                  >
                    <option value="">{t('content:senPlan.selectPlaceholder')}</option>
                    <option value="男">{t('content:senPlan.male')}</option>
                    <option value="女">{t('content:senPlan.female')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-base font-bold text-stone-700 mb-2">{t('content:senPlan.ageLabel')}</label>
                  <select
                    required
                    value={form.age}
                    onChange={e => setForm(prev => ({ ...prev, age: e.target.value }))}
                    className="w-full p-4 text-lg border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none min-h-[56px] bg-white"
                  >
                    <option value="">{t('content:senPlan.selectPlaceholder')}</option>
                    {AGE_OPTIONS.map(a => (
                      <option key={a} value={a}>{a === '25 以上' ? t('content:senPlan.age25Plus') : a}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-base font-bold text-stone-700 mb-2">{t('content:senPlan.districtLabel')}</label>
                <select
                  required
                  value={form.district}
                  onChange={e => setForm(prev => ({ ...prev, district: e.target.value }))}
                  className="w-full p-4 text-lg border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none min-h-[56px] bg-white"
                >
                  <option value="">{t('content:senPlan.districtPlaceholder')}</option>
                  {HK_DISTRICT_GROUPS.map(g => (
                    <optgroup key={g.region} label={g.region}>
                      {g.areas.map(a => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-base font-bold text-stone-700 mb-2">{t('content:senPlan.attendeesLabel')}</label>
                <div className="flex gap-4">
                  {['1', '2'].map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, attendees: n }))}
                      className={`flex-1 py-4 rounded-xl border-2 font-bold text-lg transition ${
                        form.attendees === n
                          ? 'border-[#C2410C] bg-orange-50 text-[#C2410C]'
                          : 'border-stone-200 bg-white text-stone-700 hover:border-[#C2410C]'
                      }`}
                    >
                      {t('content:senPlan.attendeesOption', { count: Number(n) })}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-base font-bold text-stone-700 mb-2">{t('content:senPlan.slotLabel')}</label>
                {slotsLoading ? (
                  <div className="flex items-center gap-2 text-stone-500 p-4">
                    <Loader2 size={20} className="animate-spin" />
                    {t('content:senPlan.loading')}
                  </div>
                ) : slots.length === 0 ? (
                  <p className="p-4 text-stone-500">{t('content:senPlan.noSlots')}</p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {slots.map(s => (
                      <button
                        key={s.slot}
                        type="button"
                        disabled={s.full}
                        onClick={() => setForm(prev => ({ ...prev, sessionSlot: s.slot }))}
                        className={`flex items-center justify-between px-4 py-4 rounded-xl border-2 font-bold transition ${
                          s.full
                            ? 'border-stone-100 bg-stone-50 text-stone-400 cursor-not-allowed'
                            : form.sessionSlot === s.slot
                              ? 'border-[#C2410C] bg-orange-50 text-[#C2410C]'
                              : 'border-stone-200 bg-white text-stone-700 hover:border-[#C2410C]'
                        }`}
                      >
                        <span className="text-base md:text-lg text-left">{s.label}</span>
                        {s.full ? (
                          <span className="text-sm bg-stone-200 text-stone-600 px-2 py-1 rounded shrink-0">{t('content:senPlan.slotFull')}</span>
                        ) : (
                          <span className="text-sm text-green-600 font-medium shrink-0">{t('content:senPlan.slotsLeft', { count: s.available })}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#C2410C] text-white text-xl font-bold py-5 rounded-xl hover:bg-[#9a3412] transition disabled:opacity-50 min-h-[64px]"
              >
                {submitting ? t('content:senPlan.submitting') : t('content:senPlan.submitBtn')}
              </button>

              <button
                type="button"
                onClick={handleCopy}
                className="w-full flex items-center justify-center gap-2 bg-white border-2 border-[#C2410C] text-[#C2410C] text-lg font-bold py-4 rounded-xl hover:bg-orange-50 transition"
              >
                <Copy size={20} />
                {t('content:senPlan.copyForwardBtn')}
              </button>
            </form>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default SenPlanPage;
